import { computed, type ComputedRef } from 'vue'
import { useTaskActivityStore } from '@/stores/taskActivityStore'

export interface EntityTaskStats {
  count: number
  recentCount: number
  lastSyncAt: string | null
  hasFailures: boolean
}

/**
 * Derives per-entity task stats from the taskActivityStore.
 *
 * PURE READ: this composable does NOT register itself with the store. It
 * does NOT start any timers. Each call returns a fresh ComputedRef that
 * re-derives on every store change.
 *
 * With no subscription active upstream, fields return zeros / null / false
 * — that is the deliberate contract. To populate `recentCount`,
 * `lastSyncAt`, and `hasFailures`, the consumer (or one of its ancestors)
 * MUST register an activity feed that includes recent tasks (see
 * `CustomersView.vue` for the canonical pattern). The store's polling
 * loop (`taskActivityStore.scheduleNextPoll`) keeps the values fresh.
 *
 * @param entityType One of 'STOCK' | 'ITEM' | 'CUSTOMER' | 'INVOICE' | 'PAYMENT'.
 *                   Lowercase / mixed-case input is uppercased internally (D-15.2).
 */
export function useEntityTaskStats(entityType: string): ComputedRef<EntityTaskStats> {
  const store = useTaskActivityStore()
  const key = entityType.toUpperCase()

  return computed<EntityTaskStats>(() => {
    const active = store.activeTasksFor(key)
    const recent = store.recentTasksFor([key])

    // Use the most recent server-side activity timestamp; updatedAt is set on
    // every task transition, so it reflects "last touched". Fall back to
    // createdAt (always set on insert). Lexical compare on Z-suffixed ISO 8601
    // works because these strings sort lexicographically by time.
    let lastSyncAt: string | null = null
    for (const t of recent) {
      const ts = t.updatedAt ?? t.createdAt
      if (lastSyncAt === null || ts > lastSyncAt) {
        lastSyncAt = ts
      }
    }

    const hasFailures = recent.some(
      t => t.status === 'failed' || t.status === 'dead_lettered'
    )

    return {
      count: active.length,
      recentCount: recent.length,
      lastSyncAt,
      hasFailures,
    }
  })
}
